import { useState, useEffect } from "react";
import api from "../api/client";
import CalendarMonth from "./CalendarMonth";

const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const fmt = (isoDate) => {
  const d = new Date(`${isoDate}T12:00:00`);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
};

const HORIZON_OPTIONS = [7, 30, 90];

const Stat = ({ label, value, tone }) => (
  <div className="glass-card rounded-2xl p-5">
    <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">{label}</p>
    <p className={`text-3xl font-bold ${tone}`}>{value}</p>
  </div>
);

// Pre-filled relance templates. Kept short & warm so the coach can still tweak.
function buildRelanceText({ student_name, reason, days, title }) {
  const firstName = (student_name || "").trim().split(/\s+/)[0] || "";
  const hi = firstName ? `Salut ${firstName},` : "Salut,";
  if (reason === "late") {
    return `${hi}\n\nJe vois que « ${title} » est en retard de ${Math.abs(days)} jour(s). On regarde ça ensemble ? Dis-moi ce qui te bloque et on trouve une solution.\n\nÀ très vite,`;
  }
  if (reason === "inactive") {
    return `${hi}\n\nCa fait ${days} jour(s) que je ne t'ai pas vu passer sur Odys'Mentoring. Tout va bien ? N'hésite pas à venir en live ou à réserver un RDV pour qu'on avance ensemble.\n\nÀ très vite,`;
  }
  if (reason === "upcoming") {
    const when = days === 0 ? "aujourd'hui" : `dans ${days} jour(s)`;
    return `${hi}\n\nPetit rappel amical : l'échéance « ${title} » arrive ${when}. Besoin d'un coup de main pour la boucler ?\n\nÀ très vite,`;
  }
  return `${hi}\n\nJuste un petit message pour prendre des nouvelles et voir où tu en es sur ton parcours.\n\nÀ très vite,`;
}

function triggerRelance(studentId, content) {
  sessionStorage.setItem(
    "odys-relance-prefill",
    JSON.stringify({ studentId, content })
  );
  window.dispatchEvent(new CustomEvent("odys-open-messages", { detail: { studentId } }));
}

function RelanceButton({ studentId, content, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={(e) => {
        e.stopPropagation();
        triggerRelance(studentId, content);
      }}
      className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-[#C9A86A]/15 text-[#8a6a2f] hover:bg-[#C9A86A]/30 transition-colors"
      title="Relancer cet élève avec un message pré-rempli"
    >
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
      Relancer
    </button>
  );
}

export default function CoachRadar({ onSelectStudent = null }) {
  const [radar, setRadar] = useState(null);
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("radar");
  const [horizon, setHorizon] = useState(30);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setLoading(true);
    api.get(`/calendar/coach-radar?horizon_days=${horizon}`)
      .then(r => { if (!cancelled) setRadar(r.data); })
      .catch(e => { if (!cancelled) setError(e.response?.data?.detail || e.message || "Erreur inconnue"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reloadKey, horizon]);

  useEffect(() => {
    api.get("/calendar/events").then(r => setEvents(r.data.events || [])).catch(() => setEvents([]));
  }, [reloadKey]);

  if (error) {
    return (
      <div className="glass-card rounded-2xl p-5" data-testid="coach-radar-error">
        <p className="text-sm font-medium text-gray-900 mb-1">Impossible de charger la vue globale</p>
        <p className="text-xs text-gray-500 mb-3">{String(error)}</p>
        <button data-testid="coach-radar-retry-btn" onClick={() => setReloadKey(k => k + 1)} className="btn-secondary text-xs">
          Réessayer
        </button>
      </div>
    );
  }

  if (!radar) return <div className="skeleton h-40 rounded-2xl" data-testid="coach-radar-loading" />;

  const Section = ({ title, items, render, testid, empty }) => (
    <div className="glass-card rounded-2xl p-5" data-testid={testid}>
      <h3 className="font-semibold text-gray-900 mb-3" style={{ fontFamily: "Georgia, serif" }}>{title}</h3>
      {items.length === 0 ? (
        <p className="text-sm text-gray-400">{empty}</p>
      ) : (
        <div className="divide-y divide-gray-100">{items.map(render)}</div>
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="coach-radar">
      <div className="flex flex-wrap items-center gap-2">
        <button
          data-testid="radar-view-btn"
          onClick={() => setView("radar")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${view === "radar" ? "bg-[#1a2744] text-white" : "text-gray-500 hover:bg-gray-100"}`}
        >Radar</button>
        <button
          data-testid="radar-calendar-view-btn"
          onClick={() => setView("calendar")}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${view === "calendar" ? "bg-[#1a2744] text-white" : "text-gray-500 hover:bg-gray-100"}`}
        >Agenda global</button>

        {view === "radar" && (
          <div className="ml-auto flex items-center gap-1 rounded-full bg-gray-100 p-1" data-testid="radar-horizon-filter">
            <span className="px-2 text-xs text-gray-500 hidden sm:inline">Échéances</span>
            {HORIZON_OPTIONS.map(h => (
              <button
                key={h}
                data-testid={`radar-horizon-${h}`}
                onClick={() => setHorizon(h)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${horizon === h ? "bg-white text-[#1a2744] shadow-sm" : "text-gray-500 hover:text-gray-800"}`}
              >
                {h} j
              </button>
            ))}
          </div>
        )}
      </div>

      {view === "calendar" ? (
        <CalendarMonth events={events} />
      ) : (
        <div className={loading ? "opacity-60 transition-opacity pointer-events-none" : "transition-opacity"} data-testid="radar-body" aria-busy={loading}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="En retard" value={radar.late.length} tone={radar.late.length ? "text-red-500" : "text-gray-900"} />
            <Stat label="Inactifs 7j+" value={radar.inactive.length} tone={radar.inactive.length ? "text-amber-500" : "text-gray-900"} />
            <Stat label={`Échéances ${horizon} j`} value={radar.upcoming.length} tone="text-[#C9A86A]" />
            <Stat label="RDV à venir" value={radar.appointments.length} tone="text-[#0F2540]" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section
              title="⚠️ Échéances dépassées"
              testid="radar-late-list"
              items={radar.late}
              empty="Aucun retard, portefeuille sain."
              render={(i) => (
                <div
                  key={i.substage_id}
                  data-testid={`radar-late-${i.substage_id}`}
                  className="flex items-center py-2.5 px-1 hover:bg-gray-50 rounded-lg"
                >
                  <button
                    onClick={() => onSelectStudent && onSelectStudent(i.student_id)}
                    className="flex-1 text-left"
                  >
                    <p className="text-sm text-gray-900"><span className="font-medium">{i.student_name}</span> — {i.title}</p>
                    <p className="text-xs text-red-500">En retard de {Math.abs(i.days)} jour(s) — {fmt(i.deadline)}</p>
                  </button>
                  <RelanceButton
                    studentId={i.student_id}
                    testid={`radar-relance-late-${i.substage_id}`}
                    content={buildRelanceText({ student_name: i.student_name, reason: "late", days: i.days, title: i.title })}
                  />
                </div>
              )}
            />

            <Section
              title="💤 Élèves inactifs"
              testid="radar-inactive-list"
              items={radar.inactive}
              empty="Tout le monde est actif."
              render={(i) => (
                <div
                  key={i.student_id}
                  data-testid={`radar-inactive-${i.student_id}`}
                  className="flex items-center py-2.5 px-1 hover:bg-gray-50 rounded-lg"
                >
                  <button
                    onClick={() => onSelectStudent && onSelectStudent(i.student_id)}
                    className="flex-1 text-left"
                  >
                    <p className="text-sm text-gray-900 font-medium">{i.student_name}</p>
                    <p className="text-xs text-amber-600">{i.days} jours sans activité</p>
                  </button>
                  <RelanceButton
                    studentId={i.student_id}
                    testid={`radar-relance-inactive-${i.student_id}`}
                    content={buildRelanceText({ student_name: i.student_name, reason: "inactive", days: i.days })}
                  />
                </div>
              )}
            />

            <Section
              title={`📅 Échéances des ${horizon} prochains jours`}
              testid="radar-upcoming-list"
              items={radar.upcoming}
              empty="Aucune échéance sur cette période — ouvrez « Agenda global » pour voir plus loin."
              render={(i) => (
                <div key={i.substage_id} className="flex items-center py-2.5 px-1" data-testid={`radar-upcoming-${i.substage_id}`}>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900"><span className="font-medium">{i.student_name}</span> — {i.title}</p>
                    <p className={`text-xs ${i.days <= 3 ? "text-[#C9A86A] font-medium" : "text-gray-500"}`}>
                      {fmt(i.deadline)} ({i.days === 0 ? "aujourd'hui" : `dans ${i.days} j`})
                    </p>
                  </div>
                  <RelanceButton
                    studentId={i.student_id}
                    testid={`radar-relance-upcoming-${i.substage_id}`}
                    content={buildRelanceText({ student_name: i.student_name, reason: "upcoming", days: i.days, title: i.title })}
                  />
                </div>
              )}
            />

            <Section
              title="🤝 Prochains rendez-vous"
              testid="radar-appointments-list"
              items={radar.appointments}
              empty="Aucun rendez-vous planifié."
              render={(i, idx) => (
                <div key={`${i.student_id}-${i.start}-${idx}`} className="py-2.5">
                  <p className="text-sm text-gray-900"><span className="font-medium">{i.student_name}</span> — {i.name}</p>
                  <p className="text-xs text-[#0F2540]">{i.start_local}</p>
                </div>
              )}
            />
          </div>

          <div className="glass-card rounded-2xl p-5 overflow-x-auto" data-testid="radar-students-table">
            <h3 className="font-semibold text-gray-900 mb-3" style={{ fontFamily: "Georgia, serif" }}>Vue par élève</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
                  <th className="py-2">Élève</th>
                  <th className="py-2">Retards</th>
                  <th className="py-2">Échéances ouvertes</th>
                  <th className="py-2">Inactivité</th>
                  <th className="py-2">Prochaine échéance</th>
                  <th className="py-2">Prochain RDV</th>
                  <th className="py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {radar.students.map(s => {
                  // Pick the actual worst late item / next upcoming item for a truthful message.
                  const worstLate = radar.late
                    .filter(i => i.student_id === s.student_id)
                    .sort((a, b) => a.days - b.days)[0];
                  const nextUpcoming = radar.upcoming
                    .filter(i => i.student_id === s.student_id)
                    .sort((a, b) => a.days - b.days)[0];
                  let content;
                  if (worstLate) {
                    content = buildRelanceText({
                      student_name: s.student_name, reason: "late",
                      days: worstLate.days, title: worstLate.title,
                    });
                  } else if (s.days_inactive >= 7) {
                    content = buildRelanceText({
                      student_name: s.student_name, reason: "inactive",
                      days: s.days_inactive,
                    });
                  } else if (nextUpcoming) {
                    content = buildRelanceText({
                      student_name: s.student_name, reason: "upcoming",
                      days: nextUpcoming.days, title: nextUpcoming.title,
                    });
                  } else {
                    content = buildRelanceText({ student_name: s.student_name, reason: "generic" });
                  }
                  return (
                    <tr key={s.student_id} data-testid={`radar-student-row-${s.student_id}`} className="hover:bg-gray-50">
                      <td className="py-2.5 font-medium text-gray-900">{s.student_name}</td>
                      <td className={`py-2.5 ${s.late_count ? "text-red-500 font-semibold" : "text-gray-400"}`}>{s.late_count}</td>
                      <td className="py-2.5 text-gray-600">{s.pending_count ?? "—"}</td>
                      <td className={`py-2.5 ${s.days_inactive >= 7 ? "text-amber-600" : "text-gray-400"}`}>
                        {s.days_inactive === null ? "—" : `${s.days_inactive} j`}
                      </td>
                      <td className="py-2.5 text-gray-600">{s.next_deadline ? fmt(s.next_deadline) : "—"}</td>
                      <td className="py-2.5 text-gray-600">{s.next_appointment ? s.next_appointment.start_local : "—"}</td>
                      <td className="py-2.5 text-right">
                        <RelanceButton
                          studentId={s.student_id}
                          testid={`radar-relance-row-${s.student_id}`}
                          content={content}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
